/**
 * useTaxiEditorInteraction — map pointer + keyboard authoring for the taxi
 * editor. Modeled on `useObstacleDragInteraction`:
 *  - place_node  : click drops a node at the cursor (current role/group)
 *  - place_edge  : click node A then node B → edge
 *  - drag/view   : drag a node to move it (total-delta, obstacle-style); click a
 *                  node/edge to select, click empty to deselect
 *  - close_segment: click an edge → toggle its enabled flag (emergency close)
 * Keyboard: Esc (view/deselect), Delete (remove selected), N/E (node/edge mode).
 *
 * Screen→scene-feet uses the proven `screenToWorldPosition` (Mapbox unproject,
 * meters → feet). Hit-testing is done in screen space by projecting each node via
 * the same path the overlay uses (CoordinateService → map.project). The scene
 * origin (`center`) is fixed at map load — never `map.getCenter()`.
 */
import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { CoordinateService } from '@/services/3d/CoordinateService'
import { screenToWorldPosition } from '@/utils/coordinates'
import { METERS_TO_FEET } from '@/constants/units'
import { useTaxiEditorStore } from '../store/taxiEditorStore'
import type { TaxiNode } from '../data/taxiGraph'

const HIT_PX = 14
const DRAG_THRESHOLD_PX = 4

interface Params {
  map: mapboxgl.Map | null
  /** Scene origin [lng, lat], fixed at map load. */
  center: [number, number] | null
  enabled: boolean
}

export function useTaxiEditorInteraction({ map, center, enabled }: Params) {
  const dragNodeId = useRef<string | null>(null)
  const dragRunwayEnd = useRef<{ id: string; endIndex: 0 | 1 } | null>(null)
  const dragStartScreen = useRef<[number, number]>([0, 0])
  const dragStartWorld = useRef<[number, number]>([0, 0])
  const dragStartNodePos = useRef<[number, number]>([0, 0])
  const moved = useRef(false)

  useEffect(() => {
    if (!map || !center || !enabled) return
    const canvas = map.getCanvas()
    const [lng, lat] = center

    const toScreen = (pos: [number, number]): [number, number] => {
      const ll = CoordinateService.scenePositionToLngLat([pos[0], 0, pos[1]], center)
      const p = map.project(ll as [number, number])
      return [p.x, p.y]
    }
    const toFeet = (clientX: number, clientY: number): [number, number] | null => {
      const rect = canvas.getBoundingClientRect()
      const ndc = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1
      )
      const w = screenToWorldPosition(ndc, map, [lng, lat])
      return w ? [w.x * METERS_TO_FEET, w.z * METERS_TO_FEET] : null
    }

    const hitNode = (clientX: number, clientY: number): TaxiNode | null => {
      const plan = useTaxiEditorStore.getState().draftPlan
      if (!plan) return null
      const rect = canvas.getBoundingClientRect()
      const px = clientX - rect.left
      const py = clientY - rect.top
      let best: TaxiNode | null = null
      let bestD = HIT_PX
      for (const n of plan.nodes) {
        const [sx, sy] = toScreen(n.pos)
        const d = Math.hypot(sx - px, sy - py)
        if (d <= bestD) {
          bestD = d
          best = n
        }
      }
      return best
    }

    const hitEdgeId = (clientX: number, clientY: number): string | null => {
      const plan = useTaxiEditorStore.getState().draftPlan
      if (!plan) return null
      const rect = canvas.getBoundingClientRect()
      const px = clientX - rect.left
      const py = clientY - rect.top
      const nodeById = new Map(plan.nodes.map((n) => [n.id, n]))
      let best: string | null = null
      let bestD = HIT_PX
      for (const e of plan.edges) {
        const a = nodeById.get(e.a)
        const b = nodeById.get(e.b)
        if (!a || !b) continue
        const [ax, ay] = toScreen(a.pos)
        const [bx, by] = toScreen(b.pos)
        const d = pointToSegment(px, py, ax, ay, bx, by)
        if (d <= bestD) {
          bestD = d
          best = e.id
        }
      }
      return best
    }

    const hitRunwayEnd = (clientX: number, clientY: number): { id: string; endIndex: 0 | 1 } | null => {
      const plan = useTaxiEditorStore.getState().draftPlan
      if (!plan) return null
      const rect = canvas.getBoundingClientRect()
      const px = clientX - rect.left
      const py = clientY - rect.top
      let best: { id: string; endIndex: 0 | 1 } | null = null
      let bestD = HIT_PX
      for (const r of plan.runways ?? []) {
        for (const endIndex of [0, 1] as const) {
          const [sx, sy] = toScreen(r.ends[endIndex].threshold)
          const d = Math.hypot(sx - px, sy - py)
          if (d <= bestD) {
            bestD = d
            best = { id: r.id, endIndex }
          }
        }
      }
      return best
    }

    const hitRunwayId = (clientX: number, clientY: number): string | null => {
      const plan = useTaxiEditorStore.getState().draftPlan
      if (!plan) return null
      const rect = canvas.getBoundingClientRect()
      const px = clientX - rect.left
      const py = clientY - rect.top
      let best: string | null = null
      let bestD = HIT_PX
      for (const r of plan.runways ?? []) {
        const [ax, ay] = toScreen(r.ends[0].threshold)
        const [bx, by] = toScreen(r.ends[1].threshold)
        const d = pointToSegment(px, py, ax, ay, bx, by)
        if (d <= bestD) {
          bestD = d
          best = r.id
        }
      }
      return best
    }

    const onMouseDown = (e: mapboxgl.MapMouseEvent) => {
      const { clientX, clientY } = e.originalEvent
      dragStartScreen.current = [clientX, clientY]
      moved.current = false
      const mode = useTaxiEditorStore.getState().mode
      // Node / runway-endpoint drag only in view/drag modes.
      if (mode === 'view' || mode === 'drag') {
        const rwy = hitRunwayEnd(clientX, clientY)
        if (rwy) {
          const plan = useTaxiEditorStore.getState().draftPlan
          const end = plan?.runways?.find((r) => r.id === rwy.id)?.ends[rwy.endIndex]
          if (end) {
            dragRunwayEnd.current = rwy
            dragStartNodePos.current = [end.threshold[0], end.threshold[1]]
            const feet = toFeet(clientX, clientY)
            if (feet) dragStartWorld.current = feet
            e.preventDefault()
            map.dragPan.disable()
            return
          }
        }
        const n = hitNode(clientX, clientY)
        if (n) {
          dragNodeId.current = n.id
          dragStartNodePos.current = [n.pos[0], n.pos[1]]
          const feet = toFeet(clientX, clientY)
          if (feet) dragStartWorld.current = feet
          e.preventDefault()
          map.dragPan.disable()
        }
      }
    }

    const onMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const { clientX, clientY } = e.originalEvent
      const mode = useTaxiEditorStore.getState().mode
      if (mode === 'place_node' || mode === 'place_runway') canvas.style.cursor = 'crosshair'
      else if (mode === 'place_edge' || mode === 'close_segment') canvas.style.cursor = 'pointer'

      if (!dragNodeId.current && !dragRunwayEnd.current) return
      const dx = clientX - dragStartScreen.current[0]
      const dy = clientY - dragStartScreen.current[1]
      if (!moved.current && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return
      moved.current = true
      canvas.style.cursor = 'move'
      const feet = toFeet(clientX, clientY)
      if (!feet) return
      const nx = dragStartNodePos.current[0] + (feet[0] - dragStartWorld.current[0])
      const nz = dragStartNodePos.current[1] + (feet[1] - dragStartWorld.current[1])
      if (dragRunwayEnd.current) {
        useTaxiEditorStore.getState().moveRunwayEnd(dragRunwayEnd.current.id, dragRunwayEnd.current.endIndex, [nx, nz])
      } else if (dragNodeId.current) {
        useTaxiEditorStore.getState().moveNode(dragNodeId.current, [nx, nz])
      }
    }

    const onMouseUp = (e: mapboxgl.MapMouseEvent) => {
      const { clientX, clientY } = e.originalEvent
      const store = useTaxiEditorStore.getState()
      const wasDragging = (dragNodeId.current || dragRunwayEnd.current) && moved.current
      dragNodeId.current = null
      dragRunwayEnd.current = null
      map.dragPan.enable()
      canvas.style.cursor = ''
      if (wasDragging) return // a drag, not a click

      const dx = clientX - dragStartScreen.current[0]
      const dy = clientY - dragStartScreen.current[1]
      if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) return // map pan, ignore

      switch (store.mode) {
        case 'place_node': {
          const feet = toFeet(clientX, clientY)
          if (feet) store.addNode(feet)
          return
        }
        case 'place_edge': {
          const n = hitNode(clientX, clientY)
          if (n) store.clickNodeForEdge(n.id)
          return
        }
        case 'close_segment': {
          const id = hitEdgeId(clientX, clientY)
          if (id) store.toggleEdgeEnabled(id)
          return
        }
        case 'place_runway': {
          const feet = toFeet(clientX, clientY)
          if (feet) store.clickForRunway(feet)
          return
        }
        default: {
          // Runway endpoint / centerline first (they sit over the network).
          const rwyEnd = hitRunwayEnd(clientX, clientY)
          if (rwyEnd) {
            store.selectRunway(rwyEnd.id)
            return
          }
          const n = hitNode(clientX, clientY)
          if (n) {
            store.selectNode(n.id)
            return
          }
          const id = hitEdgeId(clientX, clientY)
          if (id) {
            store.selectEdge(id)
            return
          }
          const rwyId = hitRunwayId(clientX, clientY)
          if (rwyId) store.selectRunway(rwyId)
          else store.clearSelection()
        }
      }
    }

    map.on('mousedown', onMouseDown)
    map.on('mousemove', onMouseMove)
    map.on('mouseup', onMouseUp)
    return () => {
      map.off('mousedown', onMouseDown)
      map.off('mousemove', onMouseMove)
      map.off('mouseup', onMouseUp)
      canvas.style.cursor = ''
      map.dragPan.enable()
    }
  }, [map, center, enabled])

  // Keyboard shortcuts.
  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      // Ignore while typing in form fields.
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      const store = useTaxiEditorStore.getState()
      if (e.key === 'Escape') {
        store.setMode('view')
        store.clearSelection()
      } else if (e.key === 'Delete') {
        store.deleteSelected()
      } else if (e.key === 'n' || e.key === 'N') {
        store.setMode('place_node')
      } else if (e.key === 'e' || e.key === 'E') {
        store.setMode('place_edge')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled])
}

/** Distance from point (px,py) to segment (ax,ay)-(bx,by). */
function pointToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const abx = bx - ax
  const aby = by - ay
  const len2 = abx * abx + aby * aby
  if (len2 < 1e-6) return Math.hypot(px - ax, py - ay)
  let t = ((px - ax) * abx + (py - ay) * aby) / len2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (ax + abx * t), py - (ay + aby * t))
}
